import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Search, BookOpen, Bookmark, StickyNote, Layers, Info, Printer, Download, Settings, Plus, BookOpenCheck } from 'lucide-react';
import type { SidebarTab } from './types';
import { useTranslation } from '../../i18n/useTranslation';

interface DockItem {
  id: SidebarTab;
  icon: React.ElementType;
  label: string;
}

interface FloatingVerticalDockProps {
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
  className?: string;
}

const DOCK_ITEM_DEFS: Array<{ id: SidebarTab; icon: React.ElementType; labelKey: string }> = [
  { id: 'search', icon: Search, labelKey: 'App.Reader.Panel.Search' },
  { id: 'toc', icon: BookOpen, labelKey: 'App.Reader.Panel.Contents' },
  { id: 'bookmarks', icon: Bookmark, labelKey: 'App.Reader.Panel.Bookmarks' },
  { id: 'notes', icon: StickyNote, labelKey: 'App.Reader.Panel.Notes' },
  { id: 'flashcards', icon: Layers, labelKey: 'App.Reader.Dock.Flashcards' },
  { id: 'print', icon: Printer, labelKey: 'App.Reader.Panel.Print' },
  { id: 'download', icon: Download, labelKey: 'App.Reader.Panel.Download' },
  { id: 'info', icon: Info, labelKey: 'App.Reader.Panel.Info' },
  { id: 'layout', icon: BookOpenCheck, labelKey: 'App.Reader.Dock.PageLayout' },
  { id: 'settings', icon: Settings, labelKey: 'App.Reader.Panel.Settings' },
  { id: 'quickAction', icon: Plus, labelKey: 'App.Reader.Dock.QuickAction' },
];

function DockIcon({ item, mouseY, isActive, onClick, compact }: {
  item: DockItem;
  mouseY: any;
  isActive: boolean;
  onClick: () => void;
  compact: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const distance = useTransform(mouseY, (val) => {
    const bounds = ref.current?.getBoundingClientRect() ?? { y: 0, height: 0 };
    return val - bounds.y - bounds.height / 2;
  });

  const sizes = compact ? [38, 52, 38] : [52, 80, 52];
  const widthSync = useTransform(distance, [-150, 0, 150], sizes);
  const width = useSpring(widthSync, { mass: 0.1, stiffness: 200, damping: 15 });

  const Icon = item.icon;
  const radius = compact ? 'rounded-xl' : 'rounded-2xl';

  return (
    <div className="relative group">
      <motion.button
        ref={ref}
        style={{ width, height: width }}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          relative flex items-center justify-center ${radius}
          transition-all duration-300 ease-out
          ${isActive
            ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/50'
            : 'bg-white/90 hover:bg-white text-neutral-700 hover:text-neutral-900 shadow-md hover:shadow-lg'
          }
          backdrop-blur-sm border border-neutral-200/50
        `}
        whileTap={{ scale: 0.92 }}
        aria-label={item.label}
        role="tab"
        aria-selected={isActive}
        tabIndex={isActive ? 0 : -1}
      >
        <motion.div
          style={{
            width: useTransform(width, (w) => w * 0.45),
            height: useTransform(width, (w) => w * 0.45),
          }}
          className="relative z-10"
        >
          <Icon className="w-full h-full" strokeWidth={isActive ? 2.5 : 2} />
        </motion.div>

        {isActive && (
          <motion.div
            layoutId="activeIndicator"
            className={`absolute top-1/2 -translate-y-1/2 w-1 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full ${compact ? '-left-1 h-5' : '-left-1.5 h-8'}`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />
        )}

        {!isActive && isHovered && (
          <motion.div
            className={`absolute inset-0 ${radius} bg-gradient-to-br from-primary-400/20 to-primary-600/20`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </motion.button>

      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className="absolute right-full mr-4 top-1/2 -translate-y-1/2 px-3 py-2
                     bg-neutral-900 text-white text-sm font-medium rounded-lg
                     pointer-events-none whitespace-nowrap shadow-xl z-50"
          >
            {item.label}
            <div className="absolute left-full top-1/2 -translate-y-1/2 -ml-1">
              <div className="w-2 h-2 bg-neutral-900 rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const NORMAL_ITEM_HEIGHT = 52;
const COMPACT_ITEM_HEIGHT = 38;
const NORMAL_GAP = 8;
const COMPACT_GAP = 4;
const NORMAL_PADDING = 10;
const COMPACT_PADDING = 6;
const DOCK_TOP_OFFSET = 100;
const DOCK_BOTTOM_MARGIN = 120;

function useCompactDock() {
  const [compact, setCompact] = useState(false);
  const itemCount = DOCK_ITEM_DEFS.length;

  const check = useCallback(() => {
    const available = window.innerHeight - DOCK_TOP_OFFSET - DOCK_BOTTOM_MARGIN;
    const normalTotal = itemCount * NORMAL_ITEM_HEIGHT + (itemCount - 1) * NORMAL_GAP + NORMAL_PADDING * 2;
    setCompact(normalTotal > available);
  }, [itemCount]);

  useEffect(() => {
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [check]);

  return compact;
}

export function FloatingVerticalDock({ activeTab, onTabChange, className = '' }: FloatingVerticalDockProps) {
  const { t } = useTranslation();
  const mouseY = useMotionValue(Infinity);
  const compact = useCompactDock();
  const dockItems: DockItem[] = DOCK_ITEM_DEFS.map((d) => ({ id: d.id, icon: d.icon, label: t(d.labelKey) }));

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      onMouseMove={(e) => mouseY.set(e.pageY)}
      onMouseLeave={() => mouseY.set(Infinity)}
      role="tablist"
      aria-orientation="vertical"
      aria-label={t('App.Reader.Dock.AriaLabel')}
      className={`
        fixed right-6 top-20 z-50
        hidden md:flex flex-col items-center
        bg-white/80 backdrop-blur-xl
        border border-neutral-200/60 shadow-2xl shadow-neutral-900/10
        overflow-y-auto
        ${compact ? 'gap-1 p-1.5 rounded-[20px]' : 'gap-2 p-2.5 rounded-[28px]'}
        ${className}
      `}
      style={{ maxHeight: 'calc(100vh - 200px)' }}
    >
      {dockItems.map((item) => (
        <DockIcon
          key={item.id}
          item={item}
          mouseY={mouseY}
          isActive={activeTab === item.id}
          onClick={() => onTabChange(activeTab === item.id ? null : item.id)}
          compact={compact}
        />
      ))}
    </motion.div>
  );
}

// Mobile version - horizontal dock at bottom
export function FloatingMobileDock({ activeTab, onTabChange, className = '' }: FloatingVerticalDockProps) {
  const { t } = useTranslation();
  const allDockItems: DockItem[] = DOCK_ITEM_DEFS.map((d) => ({ id: d.id, icon: d.icon, label: t(d.labelKey) }));
  // Show only essential items on mobile to fit screen width
  const mobileDockItems = allDockItems.filter(item =>
    ['search', 'toc', 'bookmarks', 'notes', 'flashcards', 'settings'].includes(item.id as string)
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      className={`
        fixed bottom-[68px] left-1/2 -translate-x-1/2 z-[45]
        flex md:hidden items-center gap-1 px-2 py-1.5
        bg-white/90 backdrop-blur-xl rounded-[20px]
        border border-neutral-200/60 shadow-2xl shadow-neutral-900/10
        ${className}
      `}
    >
      {mobileDockItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;

        return (
          <button
            key={item.id}
            onClick={() => onTabChange(isActive ? null : item.id)}
            className={`
              relative w-11 h-11 flex items-center justify-center rounded-xl
              transition-all duration-300
              ${isActive
                ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/40 scale-105'
                : 'bg-white/70 hover:bg-white text-neutral-700 hover:text-neutral-900 shadow-sm hover:shadow-md'
              }
              border border-neutral-200/40
            `}
            aria-label={item.label}
          >
            <Icon className="w-4.5 h-4.5" strokeWidth={isActive ? 2.5 : 2} />
            {/* Active indicator */}
            {isActive && (
              <motion.div
                layoutId="mobileActiveIndicator"
                className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              />
            )}
          </button>
        );
      })}
    </motion.div>
  );
}
